import { useEffect, useState } from 'react';
import './league-history.styles.scss';
import { Link, useParams } from 'react-router-dom';
import { listenToDoc } from '@/utils/getDataFromDb';
import { recordText } from '@/utils/leagueLabels';
import { Top25Context, top25From } from '@/utils/top25';
import TeamBadge from '@/components/SHARED/team-badge/team-badge.component';
import Standings from '@/components/SHARED/standings/standings.component';
import PostseasonView from '@/components/SHARED/postseason-view/postseason-view.component';
import LeadersView from '@/components/SHARED/leaders-view/leaders-view.component';
import ScheduleView from '@/components/SHARED/schedule-view/schedule-view.component';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import SeasonAwards from './season-awards.component';

// One finished season from seasons/{year}: final standings and rankings,
// the postseason, leaders, and the schedule with its box scores (loaded
// only when opened). Team names show how they were that season.
const PastSeason = ({ league, teamLink, gameLink }) => {

    const { year } = useParams();
    const [ season, setSeason ] = useState(undefined);
    const [ schedule, setSchedule ] = useState(null);
    const [ section, setSection ] = useState('awards');

    useEffect(() => {
        let unsub;
        setSeason(undefined);
        listenToDoc({
            'path': `leagues/${league.id}/seasons/${year}`,
            'callback': setSeason,
            'handleListener': (u) => { unsub = u; },
        });
        return () => { if (unsub) unsub(); }
    }, [league.id, year])

    useEffect(() => {
        if (section !== 'schedule' || schedule) return;
        let unsub;
        listenToDoc({
            'path': `leagues/${league.id}/seasonSchedules/${year}`,
            'callback': (d) => setSchedule(d ?? { games: [], days: 0 }),
            'handleListener': (u) => { unsub = u; },
        });
        return () => { if (unsub) unsub(); }
    }, [league.id, year, section, schedule])

    if (season === undefined) return <div className='center-col'><ThreeDotsLoader /></div>;
    if (season === null) return <p className='meta'>That season isn&apos;t in the record books. <Link to='..' relative='path'>All seasons</Link></p>;

    const pro = league.type === 'pro';
    const teams = season.teams;
    const champion = teams[season.champion];
    // The league as it was that season, for the shared views.
    const then = {
        ...league,
        conferences: season.conferences ?? league.conferences,
        season: { year: season.year, phase: 'complete', day: 0, totalDays: 0, champion: season.champion },
    };
    const ranked = Object.values(teams).sort((a, b) => (a.poll ?? 999) - (b.poll ?? 999));
    const rankings = { teams: Object.fromEntries(ranked.map(t => [t.id, { poll: t.poll }])) };
    const sections = [
        ['awards', 'Awards'],
        ['rankings', pro ? 'Final power rankings' : 'Final rankings'],
        ['standings', 'Standings'],
        ['postseason', pro ? 'Playoffs' : 'Postseason'],
        ['leaders', 'Leaders'],
        ['schedule', 'Schedule'],
    ];

    return (
        <Top25Context.Provider value={top25From(then, rankings)}>
            <div className='past-season'>
                <Link to='..' relative='path' className='back-link'>All seasons</Link>
                <div className='g-card'>
                    <h2>Season {season.year}</h2>
                    {champion ?
                        <Link to={teamLink(champion.id)} className='team-link'>
                            <TeamBadge team={champion} size='sm' /> Champion: <b>{champion.name} {champion.mascot}</b> ({recordText(champion.record)})
                        </Link> :
                        <p className='meta'>No champion was crowned.</p>}
                </div>

                <div className='section-chips' role='tablist' aria-label='Season'>
                    {sections.map(([key, label]) => (
                        <button key={key} role='tab' aria-selected={section === key}
                            className={`chip ${section === key ? 'active' : ''}`}
                            onClick={() => setSection(key)}>
                            {label}
                        </button>
                    ))}
                </div>

                {section === 'awards' &&
                    <SeasonAwards season={season} teams={teams} conferences={then.conferences} teamLink={teamLink} pro={pro} />
                }
                {section === 'rankings' &&
                    <div className='g-card'>
                        <div className='table-scroll'>
                            <table className='g-table finish-table'>
                                <thead>
                                    <tr>
                                        <th className='num'>#</th>
                                        <th>Team</th>
                                        <th className='num'>W-L</th>
                                        <th>Finish</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ranked.map(t => (
                                        <tr key={t.id} className={t.id === season.champion ? 'champ' : ''}>
                                            <td className='num'>{t.poll ?? '-'}</td>
                                            <td>
                                                <Link to={teamLink(t.id)} className='team-link'>
                                                    <TeamBadge team={t} size='sm' /> <span>{t.name}</span>
                                                </Link>
                                            </td>
                                            <td className='num'>{recordText(t.record)}</td>
                                            <td>{t.finish}{confTitles(season.confChampions?.[t.conference], t.id)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                }
                {section === 'standings' && <Standings league={then} teams={teams} teamLink={teamLink} />}
                {section === 'postseason' &&
                    <PostseasonView league={then} postseason={season.postseason} teams={teams} teamLink={teamLink} gameLink={gameLink} />
                }
                {section === 'leaders' &&
                    <LeadersView leaders={{ categories: season.leaders }} teamStats={season.teamStats} teams={teams} teamLink={teamLink} />
                }
                {section === 'schedule' &&
                    <div className='g-card'>
                        {schedule ?
                            <ScheduleView schedule={schedule} teams={teams} currentDay={schedule.days} gameLink={gameLink} /> :
                            <ThreeDotsLoader />}
                        <p className='meta'>Box scores are kept for past seasons; the play-by-play isn&apos;t.</p>
                    </div>
                }
            </div>
        </Top25Context.Provider>
    )
}

// " · Conf. champ" / " · Conf. tourney champ" after a team's finish.
function confTitles(champs, id) {
    const titles = [];
    if (champs?.regular?.includes(id)) titles.push(champs.regular.length > 1 ? 'Conf. co-champ' : 'Conf. champ');
    if (champs?.tournament === id) titles.push('Conf. tourney champ');
    return titles.length ? ` · ${titles.join(' · ')}` : '';
}

export default PastSeason
